"use client";

import React, {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";

import Slide from "@mui/material/Slide";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";

import { AssertiveA11yComponent } from "~/components/assertive-a11y-component/component";
import { Favorite24 } from "~/theme/icons/components/favorite";
import { Optional } from "~/types/general.types";

import { DEFAULT_NOTIFICATION_DURATION } from "./constant";
import { NotificationContext } from "./context";
import { NOTIFICATION_CLASSES, NotificationContainer } from "./styles";
import { NotificationOptions } from "./types";
export type NotificationProviderProps = PropsWithChildren;

export const NotificationProvider: FunctionComponent<NotificationProviderProps> = ({
  children,
}) => {
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMessageText, setNotificationMessageText] =
    useState<Optional<string>>("");
  const [isIconPresent, setIsIconPresent] = useState(true);
  const [icon, setIcon] = useState<React.JSX.Element | string>(<Favorite24 />);
  const [a11yKey, setA11yKey] = useState(0);

  const handleShowNotification = useCallback(
    (notificationMessage?: string, options?: NotificationOptions) => {
      // Clear the announcement text first, then set it after a short delay.
      // This ensures React updates the DOM in two steps, so screen readers will re-announce
      // even if the same message is shown twice in a row.
      setNotificationMessageText("");
      setA11yKey((prevKey) => prevKey + 1); // force re-mount component for iOS
      setTimeout(() => {
        setNotificationMessageText(notificationMessage);
        setIsIconPresent(options?.isIconPresent ?? true);
        setIcon(options?.icon ?? <Favorite24 />);
        setIsNotification(true);
      }, 150); // 150ms for iOS
    },
    [],
  );

  const handleCloseNotification = useCallback(() => {
    setIsNotification(false);
  }, []);

  const notification = useMemo(
    () => (
      <Snackbar
        open={isNotification}
        autoHideDuration={DEFAULT_NOTIFICATION_DURATION}
        onClose={handleCloseNotification}
        TransitionComponent={Slide}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <NotificationContainer>
          {isIconPresent && icon}
          <Typography className={NOTIFICATION_CLASSES.message}>
            {notificationMessageText}
          </Typography>
        </NotificationContainer>
      </Snackbar>
    ),
    [
      isNotification,
      notificationMessageText,
      handleCloseNotification,
      isIconPresent,
      icon,
    ],
  );

  return (
    <NotificationContext.Provider value={{ notification, handleShowNotification }}>
      {notification}
      <AssertiveA11yComponent
        key={a11yKey}
        announcementText={notificationMessageText ?? ""}
      />
      {children}
    </NotificationContext.Provider>
  );
};
